import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Flag, Loader2, ShieldAlert } from 'lucide-react';

// Admin-only: flag the whole current AI Explore conversation as wrong, attach a
// note, and file it for AI review. The full conversation (with query + engine
// trace) is attached automatically server-side from the session.
export function ReportConversationDialog({ open, onClose, sessionId, messages = [] }) {
  const [comment, setComment] = useState('');
  const [flagged, setFlagged] = useState(new Set()); // 1-based turn numbers; empty = whole conversation
  const [submitting, setSubmitting] = useState(false);

  // Derive user turns (each user question is one turn).
  const turns = [];
  let t = 0;
  (messages || []).forEach((m) => {
    if (m.role === 'user') { t += 1; turns.push({ turn: t, question: m.content || '' }); }
  });

  const toggleTurn = (n) => setFlagged((prev) => {
    const next = new Set(prev);
    next.has(n) ? next.delete(n) : next.add(n);
    return next;
  });

  const submit = async () => {
    if (!comment.trim()) { toast.error('Please explain what was wrong.'); return; }
    setSubmitting(true);
    try {
      const r = await api.reportConversation({
        session_id: sessionId,
        comment: comment.trim(),
        flagged_turns: Array.from(flagged),
      });
      toast.success(r.message || 'Report filed — AI review is running.');
      setComment(''); setFlagged(new Set());
      onClose(true);
    } catch (e) {
      toast.error(e.message || 'Could not file the report.');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-lg" data-testid="report-conversation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Flag className="w-4 h-4 text-rose-500" /> Report this conversation for AI review
          </DialogTitle>
          <DialogDescription className="sr-only">
            Attach a note and file the current conversation for AI review and proposed fixes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
            The whole conversation — every question, the answer, the query that ran, and which engine produced it — is attached automatically. An AI will review it and propose fixes for a reviewer to confirm.
          </p>
          <div>
            <label className="text-xs font-medium text-foreground">What's wrong / what should the correct answer be?</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="e.g. The renewal count used the wrong indicator column and should filter to in-force policies only."
              className="mt-1 w-full rounded-lg bg-muted/30 border border-border/50 p-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="report-comment-input"
            />
          </div>
          {turns.length > 0 && (
            <div>
              <div className="text-xs font-medium text-foreground mb-1">Which turns are wrong? <span className="text-[10px] text-muted-foreground font-normal">(none ticked = whole conversation)</span></div>
              <div className="max-h-36 overflow-auto space-y-1 pr-1">
                {turns.map((tt) => (
                  <label key={tt.turn} className="flex items-start gap-2 text-[11px] p-1.5 rounded-md hover:bg-muted/40 cursor-pointer" data-testid={`report-turn-${tt.turn}`}>
                    <input type="checkbox" checked={flagged.has(tt.turn)} onChange={() => toggleTurn(tt.turn)} className="mt-0.5" />
                    <span className="text-muted-foreground truncate">Turn {tt.turn}: {tt.question}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => onClose(false)} disabled={submitting}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={submitting || !comment.trim()} data-testid="report-submit-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Flag className="w-3.5 h-3.5 mr-1" />} File report
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
